import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { NOCStatsCards } from "../components/noc/NOCStatsCards";
import { StepTestingTab } from "../components/noc/StepTestingTab";
import { OperationLogsTab } from "../components/noc/OperationLogsTab";
import { ErrorsTab } from "../components/noc/ErrorsTab";
import { AnalyticsTab } from "../components/noc/AnalyticsTab";
import { TokenUsageTab } from "../components/noc/TokenUsageTab";
import { EmailReportTab } from "../components/noc/EmailReportTab";
import { TestDetailDialog } from "../components/noc/TestDetailDialog";
import { Activity, RefreshCw, Trash2, ListChecks, Zap, Mail } from "lucide-react";

export default function NOCDashboard() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [stepLogs, setStepLogs] = useState([]);
  const [stepStats, setStepStats] = useState([]);
  const [stepPagination, setStepPagination] = useState({ skip: 0, limit: 50, total: 0 });
  const [tokenUsage, setTokenUsage] = useState(null);
  const [tokenDays, setTokenDays] = useState(30);
  const [selectedTest, setSelectedTest] = useState(null);
  const [testDetail, setTestDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    operation_type: "",
    status: "",
    user_email: ""
  });
  const [stepFilters, setStepFilters] = useState({
    step_name: "",
    status: ""
  });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0 });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        limit: pagination.limit,
        skip: pagination.skip
      });
      if (filters.operation_type) params.append("operation_type", filters.operation_type);
      if (filters.status) params.append("status", filters.status);
      if (filters.user_email) params.append("user_email", filters.user_email);

      const response = await axios.get(`${API}/operation-logs?${params}`, { headers });
      setLogs(response.data.logs);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      toast.error("Failed to load operation logs");
    }
  }, [headers, pagination.skip, pagination.limit, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/operation-logs/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  }, [headers]);

  const fetchTokenUsage = useCallback(async (days) => {
    try {
      const response = await axios.get(`${API}/noc/token-usage?days=${days ?? 30}`, { headers });
      setTokenUsage(response.data);
    } catch (error) {
      console.error("Failed to load token usage");
    }
  }, [headers]);

  const fetchStepLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        limit: stepPagination.limit,
        skip: stepPagination.skip
      });
      if (stepFilters.step_name) params.append("step_name", stepFilters.step_name);
      if (stepFilters.status) params.append("status", stepFilters.status);

      const response = await axios.get(`${API}/step-testing-logs?${params}`, { headers });
      setStepLogs(response.data.logs);
      setStepStats(response.data.step_stats || []);
      setStepPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error("Failed to load step logs");
    }
  }, [headers, stepPagination.skip, stepPagination.limit, stepFilters]);

  const handleTokenDaysChange = (d) => {
    setTokenDays(d);
    fetchTokenUsage(d);
  };

  const fetchTestDetail = async (testId) => {
    try {
      const response = await axios.get(`${API}/step-testing-logs/${testId}/detail`, { headers });
      setTestDetail(response.data);
      setSelectedTest(testId);
    } catch (error) {
      toast.error("Failed to load test detail");
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLogs(), fetchStats(), fetchStepLogs(), fetchTokenUsage(tokenDays)]);
      setLoading(false);
    };
    loadData();
  }, [fetchLogs, fetchStats, fetchStepLogs, fetchTokenUsage]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchLogs(), fetchStats(), fetchStepLogs(), fetchTokenUsage(tokenDays)]);
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleClearOldLogs = async (days) => {
    if (!window.confirm(`Clear logs older than ${days} days?`)) return;
    try {
      const response = await axios.delete(`${API}/operation-logs/clear?days_old=${days}`, { headers });
      toast.success(response.data.message);
      handleRefresh();
    } catch (error) {
      toast.error("Failed to clear logs");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="noc-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="w-6 h-6 text-red-500" />
            NOC Dashboard
          </h2>
          <p className="text-muted-foreground">System monitoring, operation logging & step testing analysis</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Select onValueChange={(v) => handleClearOldLogs(parseInt(v))}>
            <SelectTrigger className="w-[140px]">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Logs
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Older than 7 days</SelectItem>
              <SelectItem value="30">Older than 30 days</SelectItem>
              <SelectItem value="90">Older than 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <NOCStatsCards stats={stats} />

      <Tabs defaultValue="step-testing" className="space-y-4">
        <TabsList>
          <TabsTrigger value="step-testing" className="flex items-center gap-2">
            <ListChecks className="w-4 h-4" />
            Step Testing
          </TabsTrigger>
          <TabsTrigger value="logs">Operation Logs</TabsTrigger>
          <TabsTrigger value="errors">Recent Errors</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="email-report" className="flex items-center gap-2">
            <Mail className="h-3.5 w-3.5" />Email Report
          </TabsTrigger>
          <TabsTrigger value="token-usage" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Token Usage
          </TabsTrigger>
        </TabsList>

        <TabsContent value="step-testing">
          <StepTestingTab
            stepStats={stepStats}
            stepLogs={stepLogs}
            stepFilters={stepFilters}
            setStepFilters={setStepFilters}
            stepPagination={stepPagination}
            setStepPagination={setStepPagination}
            fetchStepLogs={fetchStepLogs}
            onViewDetail={fetchTestDetail}
          />
        </TabsContent>

        <TabsContent value="logs">
          <OperationLogsTab
            logs={logs}
            filters={filters}
            setFilters={setFilters}
            pagination={pagination}
            setPagination={setPagination}
            fetchLogs={fetchLogs}
          />
        </TabsContent>

        <TabsContent value="errors">
          <ErrorsTab stats={stats} />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab stats={stats} />
        </TabsContent>

        <TabsContent value="email-report">
          <EmailReportTab />
        </TabsContent>

        <TabsContent value="token-usage">
          <TokenUsageTab
            data={tokenUsage}
            days={tokenDays}
            setDays={handleTokenDaysChange}
          />
        </TabsContent>
      </Tabs>

      <TestDetailDialog
        selectedTest={selectedTest}
        setSelectedTest={setSelectedTest}
        testDetail={testDetail}
      />
    </div>
  );
}
